/**
 * A tiny color manipulation library.
 *
 * This is especially useful for dealing with rewriting LESS-color
 * manipulations in inline styles.
 */
import {Errors, KAError} from "@khan/logging";

const colorRegexp = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

export interface Color {
    r: number;
    g: number;
    b: number;
    a: number;
}

// Parse a color in #abcdef form into an object with r,g,b,a keys.
// TODO(emily): Add parsing for other formats as well.
export function parse(color: string): {
    a: number;
    b: number;
    g: number;
    r: number;
} {
    const match = color.match(colorRegexp);
    if (match) {
        return {
            r: parseInt(match[1], 16) / 255,
            g: parseInt(match[2], 16) / 255,
            b: parseInt(match[3], 16) / 255,
            a: 1,
        };
    }
    throw new KAError(`Failed to parse color: ${color}`, Errors.Internal);
}

// Darken a color by a percentage.
export function darken(
    color: Color,
    percentage: number,
): {
    a: number;
    b: number;
    g: number;
    r: number;
} {
    return {
        r: color.r * (1 - percentage),
        g: color.g * (1 - percentage),
        b: color.b * (1 - percentage),
        a: color.a,
    };
}

// Lighten a color by a percentage.
export function lighten(
    color: Color,
    percentage: number,
): {
    a: number;
    b: number;
    g: number;
    r: number;
} {
    return {
        r: color.r * (1 + percentage),
        g: color.g * (1 + percentage),
        b: color.b * (1 + percentage),
        a: color.a,
    };
}

// Set the alpha value of a color.
export function fade(
    color: Color,
    percentage: number,
): {
    a: number;
    b: number;
    g: number;
    r: number;
} {
    return {
        ...color,
        a: percentage,
    };
}

// Mix a color into a background color, using the alpha channel of the base
// color to determine the linear blend.
export function mix(
    color: Color,
    background: Color,
): {
    a: number;
    b: number;
    g: number;
    r: number;
} {
    return {
        r: color.r * color.a + background.r * (1 - color.a),
        g: color.g * color.a + background.g * (1 - color.a),
        b: color.b * color.a + background.b * (1 - color.a),
        a: 1,
    };
}

// Mix with black.
// This differs from "darken" because mixing with black desaturates,
// whereas simply lowering the numbers tends to increase saturation.
export function mixWithBlack(
    color: Color,
    amount: number,
): {
    a: number;
    b: number;
    g: number;
    r: number;
} {
    const black = {r: 0, g: 0, b: 0, a: amount} as const;

    return mix(black, color);
}

// Mix with white.
// This differs from "lighten" because mixing with white desaturates,
// whereas simply lowering the numbers tends to increase saturation.
export function mixWithWhite(
    color: Color,
    amount: number,
): {
    a: number;
    b: number;
    g: number;
    r: number;
} {
    const white = {r: 1, g: 1, b: 1, a: amount} as const;

    return mix(white, color);
}

// Stringify the color in an `rgba()` format.
export function format(color: Color): string {
    return (
        "rgba(" +
        `${Math.floor(color.r * 255)}, ${Math.floor(color.g * 255)}, ` +
        `${Math.floor(color.b * 255)}, ${color.a.toFixed(2)})`
    );
}
